import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'notificationSettingsTab';

const WarningIcon = () => (
  <svg className="h-5 w-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const TABS = [
  { id: 'channels', label: 'Channels' },
  { id: 'strategies', label: 'Strategies' },
  { id: 'movers', label: 'Movers' },
];

const TIMEZONES = [
  { value: 'Pacific/Honolulu', label: '(UTC-10:00) Hawaii' },
  { value: 'America/Anchorage', label: '(UTC-09:00) Alaska' },
  { value: 'America/Los_Angeles', label: '(UTC-08:00) Los Angeles, Vancouver' },
  { value: 'America/Denver', label: '(UTC-07:00) Denver, Phoenix' },
  { value: 'America/Chicago', label: '(UTC-06:00) Chicago, Mexico City' },
  { value: 'America/New_York', label: '(UTC-05:00) New York, Toronto' },
  { value: 'America/Sao_Paulo', label: '(UTC-03:00) São Paulo, Buenos Aires' },
  { value: 'Atlantic/Cape_Verde', label: '(UTC-01:00) Cape Verde' },
  { value: 'UTC', label: '(UTC+00:00) UTC' },
  { value: 'Europe/London', label: '(UTC+00:00) London, Dublin, Lisbon' },
  { value: 'Europe/Paris', label: '(UTC+01:00) Paris, Berlin, Amsterdam, Rome' },
  { value: 'Europe/Helsinki', label: '(UTC+02:00) Helsinki, Kyiv, Athens, Cairo' },
  { value: 'Europe/Moscow', label: '(UTC+03:00) Moscow, Istanbul' },
  { value: 'Asia/Dubai', label: '(UTC+04:00) Dubai, Baku' },
  { value: 'Asia/Karachi', label: '(UTC+05:00) Karachi, Tashkent' },
  { value: 'Asia/Kolkata', label: '(UTC+05:30) Mumbai, Delhi, Colombo' },
  { value: 'Asia/Dhaka', label: '(UTC+06:00) Dhaka, Almaty' },
  { value: 'Asia/Bangkok', label: '(UTC+07:00) Bangkok, Jakarta, Hanoi' },
  { value: 'Asia/Singapore', label: '(UTC+08:00) Singapore, Hong Kong, Perth' },
  { value: 'Asia/Shanghai', label: '(UTC+08:00) Shanghai, Taipei, Manila' },
  { value: 'Asia/Tokyo', label: '(UTC+09:00) Tokyo, Seoul' },
  { value: 'Australia/Sydney', label: '(UTC+10:00) Sydney, Melbourne' },
  { value: 'Pacific/Auckland', label: '(UTC+12:00) Auckland, Fiji' },
];

const NotificationSettingsForm = ({
  formData,
  validation,
  updating,
  handleInputChange,
  handleSubmit,
  showMoverAlerts = false,
  compact = false,
  onCancel,
}) => {
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && TABS.some(t => t.id === saved)) return saved;
    } catch {}
    return 'channels';
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, activeTab);
    } catch {}
  }, [activeTab]);

  const labelClass = compact
    ? 'text-sm text-gray-900 dark:text-white'
    : 'text-base font-medium text-gray-900 dark:text-white';
  const descClass = compact
    ? 'text-xs text-gray-500 dark:text-gray-400'
    : 'text-sm text-gray-600 dark:text-gray-400';
  const inputPadding = compact ? 'px-2 py-1.5 text-sm' : 'px-3 py-2';
  const borderLeft = compact ? 'pl-3' : 'pl-4';
  const spacing = compact ? 'space-y-3' : 'space-y-4';

  const visibleTabs = showMoverAlerts ? TABS : TABS.filter(t => t.id !== 'movers');

  const renderChannelsTab = () => (
    <div className={spacing}>
      {/* Telegram */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label htmlFor="telegramEnabled" className={labelClass}>
            Enable Telegram
          </label>
          <input
            type="checkbox"
            id="telegramEnabled"
            name="telegramEnabled"
            checked={formData.telegramEnabled}
            onChange={handleInputChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>
        
        {formData.telegramEnabled && (
          <div className={`ml-0 ${borderLeft} border-l-2 border-blue-200 dark:border-blue-800`}>
            <label htmlFor="telegramChatID" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Telegram Chat ID
            </label>
            <div className="relative">
              <input
                type="text"
                id="telegramChatID"
                name="telegramChatID"
                value={formData.telegramChatID}
                onChange={handleInputChange}
                placeholder="e.g. 123456789"
                className={`w-full ${inputPadding} border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${
                  formData.telegramChatID && !validation.telegramValid
                    ? 'border-yellow-500 dark:border-yellow-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formData.telegramChatID && !validation.telegramValid && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <WarningIcon />
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Get your Chat ID from{' '}
              <a 
                href="https://t.me/moonready_bot" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                @moonready_bot
              </a>
              {!compact && ' - send /start to receive your ID'}
            </p>
          </div>
        )}
      </div>

      {/* Email */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label htmlFor="emailEnabled" className={labelClass}>
            Enable Email
          </label>
          <input
            type="checkbox"
            id="emailEnabled"
            name="emailEnabled"
            checked={formData.emailEnabled}
            onChange={handleInputChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>
        
        {formData.emailEnabled && (
          <div className={`ml-0 ${borderLeft} border-l-2 border-blue-200 dark:border-blue-800`}>
            <label htmlFor="emailAddress" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                id="emailAddress"
                name="emailAddress"
                value={formData.emailAddress}
                onChange={handleInputChange}
                placeholder="your@email.com"
                className={`w-full ${inputPadding} border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${
                  formData.emailAddress && !validation.emailValid
                    ? 'border-yellow-500 dark:border-yellow-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formData.emailAddress && !validation.emailValid && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <WarningIcon />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );

  const renderStrategiesTab = () => (
    <div className={spacing}>
      {!validation.canEnableStrategies && (
        <p className={`${descClass} italic p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md`}>
          Enable a notification channel in the Channels tab first.
        </p>
      )}

      {/* Exit Strategy */}
      <div className={`space-y-2 ${!validation.canEnableStrategies ? 'opacity-50' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <label htmlFor="exitLevelNotifications" className={labelClass}>
              Exit Strategy Notifications
            </label>
            <p className={descClass}>
              {compact ? 'When take profit targets are reached' : 'Get notified when your take profit price targets are reached'}
            </p>
          </div>
          <div className="ml-6">
            <input
              type="checkbox"
              id="exitLevelNotifications"
              name="exitLevelNotifications"
              checked={formData.exitLevelNotifications}
              onChange={handleInputChange}
              disabled={!validation.canEnableStrategies}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
        
        {formData.exitLevelNotifications && validation.canEnableStrategies && (
          <div className={`ml-0 ${borderLeft} border-l-2 border-green-200 dark:border-green-800`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label htmlFor="exitNotifyApproaching" className="text-xs text-gray-700 dark:text-gray-300">
                  Also notify when approaching
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {compact ? 'Within 10% of target' : 'Get a heads-up when price is within 10% of your target'}
                </p>
              </div>
              <div className="ml-6">
                <input
                  type="checkbox"
                  id="exitNotifyApproaching"
                  name="exitNotifyApproaching"
                  checked={formData.exitNotifyApproaching}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invalidation Strategy */}
      <div className={`space-y-2 ${!validation.canEnableStrategies ? 'opacity-50' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <label htmlFor="invalidationNotifications" className={labelClass}>
              Invalidation {!compact && 'Strategy '}Notifications
            </label>
            <p className={descClass}>
              {compact ? 'When stop-loss or time limits are reached' : 'Get notified when stop-loss or time limits are reached'}
            </p>
          </div>
          <div className="ml-6">
            <input
              type="checkbox"
              id="invalidationNotifications"
              name="invalidationNotifications"
              checked={formData.invalidationNotifications}
              onChange={handleInputChange}
              disabled={!validation.canEnableStrategies}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
        
        {formData.invalidationNotifications && validation.canEnableStrategies && (
          <div className={`ml-0 ${borderLeft} border-l-2 border-green-200 dark:border-green-800`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label htmlFor="invalidationNotifyApproaching" className="text-xs text-gray-700 dark:text-gray-300">
                  Also notify when approaching
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {compact ? 'Within 10% of limit' : 'Get a heads-up when price or time is within 10% of your limits'}
                </p>
              </div>
              <div className="ml-6">
                <input
                  type="checkbox"
                  id="invalidationNotifyApproaching"
                  name="invalidationNotifyApproaching"
                  checked={formData.invalidationNotifyApproaching}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderMoversTab = () => (
    <div className={spacing}>
      {!validation.canEnableStrategies && (
        <p className={`${descClass} italic p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md`}>
          Enable a notification channel in the Channels tab first.
        </p>
      )}

      {/* Rapid Movers */}
      <div className={`space-y-2 ${!validation.canEnableStrategies ? 'opacity-50' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <label htmlFor="rapidMoversEnabled" className={labelClass}>
              Rapid Mover Alerts
            </label>
            <p className={descClass}>
              Get notified when positions move significantly within an hour
            </p>
          </div>
          <div className="ml-6">
            <input
              type="checkbox"
              id="rapidMoversEnabled"
              name="rapidMoversEnabled"
              checked={formData.rapidMoversEnabled}
              onChange={handleInputChange}
              disabled={!validation.canEnableStrategies}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      {/* Daily Digest */}
      <div className={`space-y-2 ${!validation.canEnableStrategies ? 'opacity-50' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <label htmlFor="dailyDigestEnabled" className={labelClass}>
              Daily Digest
            </label>
            <p className={descClass}>
              Receive a daily summary of your portfolio's top movers
            </p>
          </div>
          <div className="ml-6">
            <input
              type="checkbox"
              id="dailyDigestEnabled"
              name="dailyDigestEnabled"
              checked={formData.dailyDigestEnabled}
              onChange={handleInputChange}
              disabled={!validation.canEnableStrategies}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Digest Settings */}
        {formData.dailyDigestEnabled && validation.canEnableStrategies && (
          <div className={`ml-0 ${borderLeft} border-l-2 border-purple-200 dark:border-purple-800 space-y-3`}>
            {/* Timezone */}
            <div>
              <label htmlFor="timezone" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Timezone
              </label>
              <select
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white`}
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Auto-detected from your browser. Change if needed.
              </p>
            </div>

            {/* Digest Hour */}
            <div>
              <label htmlFor="dailyDigestHour" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Delivery Time
              </label>
              <select
                id="dailyDigestHour"
                name="dailyDigestHour"
                value={formData.dailyDigestHour}
                onChange={(e) => handleInputChange({ target: { name: 'dailyDigestHour', value: parseInt(e.target.value, 10), type: 'number' } })}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white`}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {i === 0 ? '12:00 AM (midnight)' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM (noon)' : `${i - 12}:00 PM`}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Daily digest will be sent around this time in your timezone.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'channels' && renderChannelsTab()}
        {activeTab === 'strategies' && renderStrategiesTab()}
        {activeTab === 'movers' && renderMoversTab()}
      </div>

      {/* Submit Button */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 flex justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className={`${compact ? 'px-3 py-1.5 text-sm' : 'px-4 py-2 text-sm'} rounded-md bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600`}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={updating}
          className={`inline-flex items-center ${compact ? 'px-3 py-1.5 text-sm' : 'px-4 py-2 text-sm'} font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {updating ? (
            <>
              {!compact && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              Saving...
            </>
          ) : (
            compact ? 'Save' : 'Save Settings'
          )}
        </button>
      </div>
    </form>
  );
};

export default NotificationSettingsForm;
